import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import ArrowButton from './ArrowButton';
import useMobile from '@/hooks/useMobile';

function Carousel({ render, relativeSize, padding }) {
  const childRef = useRef(null);
  const elements = render();
  const [childSize, setChildSize] = useState({ width: 0, height: 0 });
  const [index, setIndex] = useState(0);
  const [translate, setTranslate] = useState(padding);
  const { isMobile } = useMobile();

  const onNext = () => {
    if (index < elements.length - 1) {
      setIndex(index + 1);
    }
  };

  const onBack = () => {
    if (index > 0) {
      setIndex(index - 1);
    }
  };

  useEffect(() => {
    setTranslate(-(childSize.width * relativeSize * index - padding));
  }, [index, childRef, setTranslate]);

  useEffect(() => {
    if (childRef.current) {
      const { offsetWidth, offsetHeight } = childRef.current;
      setChildSize({ width: offsetWidth, height: offsetHeight });
    }
  }, []);

  return (
    <Box
      ref={childRef}
      sx={{
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {childSize.width > 0 && (
        <>
          <Box
            sx={{
              position: 'absolute',
              height: `100%`,
              width: isMobile ? '30px' : '60px',
              background:
                'linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0.527907) 45.83%, rgba(255, 255, 255, 0) 100%)',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <ArrowButton onClick={onBack} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              height: `100%`,
              width: isMobile ? '30px' : '80px',
              background:
                'linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0.527907) 45.83%, rgba(255, 255, 255, 0) 100%)',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <ArrowButton variant="right" onClick={onNext} />
          </Box>

          <Box
            sx={{
              width: `${childSize.width * relativeSize * elements.length}px`,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              transition: 'transform 0.3s',
              transitionTimingFunction: 'ease-out',
              transform: `translate(${translate}px)`,
            }}
          >
            {elements.map((item, index) => (
              <Box
                key={index}
                sx={{
                  width: `${childSize.width * relativeSize}px`,
                  mr: 1,
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

Carousel.propTypes = {
  render: PropTypes.func,
  relativeSize: PropTypes.number,
  padding: PropTypes.number,
};

Carousel.defaultProps = {
  render: () => [],
  relativeSize: 0.8,
  padding: 34,
};

export default Carousel;
